import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import { token } from '@atlaskit/tokens';
import { messages } from '@databases/intl/messages';
import Z_INDEX from '@databases/layout/z-index';
import { IGNORED_TABLE_ELEMENT_CLASS } from '@databases/table-resizer';
import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import type { IntlShape } from 'react-intl-next';

import { getBodyFromTable, getTableFromBodyRow } from '../../../table';

import { DisabledTooltipContainer } from './disabled-tooltip-container';

const SELECT_BUTTON_PADDING = 9;
const SELECT_BUTTON_WIDTH = 15;

interface BuildDragImageFromStateArgs {
  e: React.DragEvent;
  selectedRows: string[];
  trRef: React.RefObject<HTMLTableRowElement>;
  dragImageRef: React.MutableRefObject<HTMLElement | undefined>;
  dragImageRootRef: React.MutableRefObject<Root | undefined>;
  containerWidth: number;
  isSortingActive: boolean;
  formatMessage: IntlShape['formatMessage'];
}

/**
 * Builds the drag image from selections state by finding selected rows
 * via data-entry-id. Mirrors the legacy visual structure but adds a blue
 * highlight border that excludes the drag handle.
 */
export const buildEntryDragImageFromState = ({
  e,
  selectedRows,
  trRef,
  dragImageRef,
  dragImageRootRef,
  containerWidth,
  isSortingActive,
  formatMessage
}: BuildDragImageFromStateArgs) => {
  const tr = e.target as HTMLTableRowElement;
  const table = getTableFromBodyRow(tr);
  const tbody = getBodyFromTable(table);

  const selectedTrs = selectedRows
    .map((id) => tbody?.querySelector<HTMLTableRowElement>(`tr[data-entry-id="${id}"]`))
    .filter((el): el is HTMLTableRowElement => el !== null);
  const currentIndexInSelectedTrs = selectedTrs.indexOf(trRef!.current!);
  const selectedTrsIndexOffsetHeight = selectedTrs
    .slice(0, currentIndexInSelectedTrs + 1)
    .reduce((p, c) => p + c.clientHeight, 0);

  // Clone selected rows from the live DOM
  const clonedSelectedTrs = selectedTrs.map((el) => el.cloneNode(true) as HTMLElement);

  const originalWidths = Array.from(tr.cells).map((cell) => cell.offsetWidth);
  const totalWidth = originalWidths.reduce((sum, w) => sum + w, 0);
  const gridTemplate = originalWidths.map((w) => `${w}px`).join(' ');

  // Measure handle and actions cell widths for overlay positioning
  const firstRowCells = clonedSelectedTrs[0]
    ? (Array.from(clonedSelectedTrs[0].children) as HTMLElement[])
    : [];
  const handleCellWidth = firstRowCells[0]?.classList.contains(
    IGNORED_TABLE_ELEMENT_CLASS
  )
    ? (originalWidths[0] ?? 0)
    : 0;
  const lastIdx = firstRowCells.length - 1;
  const actionsCellWidth =
    lastIdx >= 0 &&
    firstRowCells[lastIdx]?.classList.contains(IGNORED_TABLE_ELEMENT_CLASS)
      ? (originalWidths[lastIdx] ?? 0)
      : 0;

  // Set ignored elements to width 0 and apply grid
  clonedSelectedTrs.forEach((el) => {
    el.style.gridTemplateColumns = gridTemplate;
    el.querySelectorAll<HTMLElement>(`.${IGNORED_TABLE_ELEMENT_CLASS}`).forEach(
      (ignored) => {
        ignored.style.width = '0';
      }
    );
  });

  // Build table
  const tBody = document.createElement('tbody');
  const tableClone = table?.cloneNode(false) as HTMLTableElement;
  tableClone.style.backgroundColor = 'transparent';

  // Make entry prefix backgrounds transparent so the blue overlay shows through
  clonedSelectedTrs.forEach((el) => {
    const entryPrefixBorder = el.querySelector(
      '.entry-number-table-border'
    ) as HTMLElement;
    if (entryPrefixBorder) {
      entryPrefixBorder.style.marginTop = '0px';
    }
    const entryPrefixCell = el.querySelector('.entry-number-table-data') as HTMLElement;
    if (entryPrefixCell) {
      entryPrefixCell.style.background = 'transparent';
    }
  });

  tableClone.append(tBody);
  // eslint-disable-next-line canvas/no-spread-arguments
  tBody.append(...clonedSelectedTrs);

  // Build container
  dragImageRef.current = document.createElement('div');
  table?.parentElement?.append(dragImageRef.current);
  dragImageRef.current.append(tableClone);

  // Blue selection overlay positioned over entry prefix + data cells
  // Use the actual rendered position of the entry prefix for precise alignment
  const entryPrefixEl = dragImageRef.current.querySelector(
    '.entry-number-table-data'
  ) as HTMLElement | null;
  const overlayLeft = handleCellWidth + actionsCellWidth;
  const overlayWidth = entryPrefixEl
    ? totalWidth - entryPrefixEl.offsetLeft - actionsCellWidth
    : totalWidth - handleCellWidth - actionsCellWidth;
  const selectionOverlay = document.createElement('div');
  Object.assign(selectionOverlay.style, {
    position: 'absolute',
    top: '0',
    left: `${overlayLeft}px`,
    width: `${overlayWidth}px`,
    height: '100%',
    border: `2px solid ${token('color.border.selected', '#0C66E4')}`,
    borderRadius: token('radius.small', '3px'),
    background: `color-mix(in srgb, ${token(
      'color.background.accent.blue.subtlest',
      '#E9F2FF'
    )} 25%, transparent)`,
    pointerEvents: 'none',
    boxSizing: 'border-box'
  });
  dragImageRef.current.append(selectionOverlay);

  if (!isSortingActive) {
    Object.assign(dragImageRef.current.style, {
      width: `${Math.min(totalWidth, containerWidth)}px`,
      overflow: 'hidden',
      position: 'absolute',
      top: 0,
      left: 0,
      zIndex: Z_INDEX.DRAG_IMAGE_ELEMENT,
      paddingLeft: `${SELECT_BUTTON_PADDING}px`
    });
    e.dataTransfer.setDragImage(
      dragImageRef.current,
      SELECT_BUTTON_WIDTH / 2,
      selectedTrsIndexOffsetHeight - tr?.clientHeight / 2
    );
  } else {
    dragImageRootRef.current = createRoot(dragImageRef.current);
    Object.assign(dragImageRef.current.style, {
      position: 'absolute',
      top: 0,
      left: 0,
      zIndex: Z_INDEX.DRAG_IMAGE_ELEMENT
    });
    dragImageRootRef.current.render(
      <DisabledTooltipContainer isEntryCardActionBar={false}>
        <CrossCircleIcon label="" color={token('color.icon.danger', '#C9372C')} />
        <div>
          {formatMessage(messages['table-layout.reset-sorting-to-enable-row-dragging'])}
        </div>
      </DisabledTooltipContainer>
    );
    e.dataTransfer.setDragImage(dragImageRef.current, 0, 25);
  }
};
